// SOP (Standard Operating Procedure) rules — single source of truth for
// computing the team's internal `due_date` from the client-facing `use_date`.
//
// Used by:
//   • CalendarsPage.js — initial creation + drag-and-drop reschedule
//   • TaskModal.js     — edit flow (when admin changes use_date)
//
// Contract: All inputs/outputs are JS Date objects (NOT strings).
// Callers are responsible for converting to/from 'YYYY-MM-DD' for the wire.

export const SOP_OPTIONS = [
  { value: 'same_day', label: 'Same day' },
  { value: '5_days_before', label: '5 days before' },
  { value: '7_days_before', label: '7 days before' },
  { value: '15_days_before', label: '15 days before' },
  { value: '30_days_before', label: '30 days before' },
  { value: 'friday_before', label: 'Friday before' },
];

const formatDateToLocal = (date) => {
  // YYYY-MM-DD in local timezone (matches HTML5 <input type="date">)
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const isNonWorkingDay = (date, holidays = []) => {
  const dayOfWeek = date.getDay();
  if (dayOfWeek === 0 || dayOfWeek === 6) return true;
  if (!Array.isArray(holidays)) return false;
  const dateStr = formatDateToLocal(date);
  return holidays.some((h) => h && h.date === dateStr);
};

export const getNearestWorkingDayBefore = (date, holidays = []) => {
  let current = new Date(date);
  current.setDate(current.getDate() - 1);
  let safety = 0;
  while (isNonWorkingDay(current, holidays) && safety < 30) {
    current.setDate(current.getDate() - 1);
    safety += 1;
  }
  return current;
};

// Compute the due date from a use date + SOP rule.
// Adjusts to the nearest working day (skips weekends + holidays).
export const calculateDueDate = (useDate, sopRule, holidays = []) => {
  const useDt = useDate instanceof Date ? new Date(useDate) : new Date(useDate);
  let dueDate = new Date(useDt);

  switch (sopRule) {
    case 'same_day':
      // No shift
      break;
    case '5_days_before':
      dueDate.setDate(dueDate.getDate() - 5);
      break;
    case '7_days_before':
      dueDate.setDate(dueDate.getDate() - 7);
      break;
    case '15_days_before':
      dueDate.setDate(dueDate.getDate() - 15);
      break;
    case '30_days_before':
      dueDate.setDate(dueDate.getDate() - 30);
      break;
    case 'friday_before':
      while (dueDate.getDay() !== 5) {
        dueDate.setDate(dueDate.getDate() - 1);
      }
      if (dueDate.getTime() === useDt.getTime()) {
        dueDate.setDate(dueDate.getDate() - 7);
      }
      break;
    default:
      // Unknown / null SOP — keep due_date == use_date (no buffer)
      break;
  }

  if (sopRule && sopRule !== 'same_day' && isNonWorkingDay(dueDate, holidays)) {
    dueDate = getNearestWorkingDayBefore(dueDate, holidays);
  }
  return dueDate;
};

// When dragging a task to a new use_date and the task has NO stored SOP,
// preserve the original gap between use_date and due_date.
export const calculateDueDateByGap = (newUseDate, oldUseDate, oldDueDate) => {
  if (!oldUseDate || !oldDueDate) return new Date(newUseDate);
  const oldUse = new Date(oldUseDate);
  const oldDue = new Date(oldDueDate);
  const gapDays = Math.round((oldUse.getTime() - oldDue.getTime()) / 86400000);
  const newDue = new Date(newUseDate);
  newDue.setDate(newDue.getDate() - gapDays);
  return newDue;
};

export { formatDateToLocal };
